import React, { useState, useMemo, useEffect } from 'react';
import { Typography } from '../../interfaces/typography';
import { UserDataResult } from '../../interfaces/users';
import { SearchInput } from '../../utils/CommonUIComponents';

interface PriorityLevelProps {
  level: number;
  users: UserDataResult[];
  pendingChanges: Map<string, number | null>;
  onMoveUser: (userId: string, direction: 'up' | 'down') => void;
  onRemoveUser: (userId: string) => void;
  isHighest: boolean;
  rankLabel: string;
  defaultCollapsed?: boolean;
}

export function PriorityLevel({
  level,
  users,
  pendingChanges,
  onMoveUser,
  onRemoveUser,
  isHighest,
  rankLabel,
  defaultCollapsed = false
}: PriorityLevelProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [searchTerm, setSearchTerm] = useState('');

  // Update collapsed state when defaultCollapsed prop changes
  useEffect(() => {
    setIsCollapsed(defaultCollapsed);
  }, [defaultCollapsed]);

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users;
    
    return users.filter(user => 
      user.user_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [users, searchTerm]);

  // Count users with pending changes
  const modifiedUsersCount = users.filter(user => pendingChanges.has(user.user_id)).length;

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const shouldShowSearch = users.length > 2;

  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200">
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={handleToggleCollapse}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
              {level}
            </div>
            <div>
              <Typography variant="h4" color="#1F2937" fontWeight={600}>
                Priority Level {level}
              </Typography>
              <div className="flex items-center gap-2">
                <Typography variant="smallParagraph" color="#6B7280">
                  {users.length} user{users.length !== 1 ? 's' : ''}
                </Typography>
                {modifiedUsersCount > 0 && (
                  <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">
                    {modifiedUsersCount} modified
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Typography variant="smallParagraph" color="#6B7280">
              {rankLabel}
            </Typography>
            <div className="flex items-center gap-2">
              {searchTerm && (
                <Typography variant="smallParagraph" color="#6B7280">
                  {filteredUsers.length} of {users.length} shown
                </Typography>
              )}
              <svg 
                className={`w-4 h-4 text-gray-500 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Collapsible Content */}
      {!isCollapsed && (
        <div className="px-4 pb-4">
          {/* Search Bar */}
          {shouldShowSearch && (
            <div className="mb-4">
              <SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder={`Search within Priority Level ${level}...`}
                className="w-full max-w-md"
              />
            </div>
          )}

          {/* Users List */}
          <div className="space-y-2">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-4">
                <Typography variant="smallParagraph" color="#6B7280">
                  {searchTerm ? 'No users match your search' : 'No users in this level'}
                </Typography>
              </div>
            ) : (
              filteredUsers.map((user) => {
                const hasChange = pendingChanges.has(user.user_id);
                return (
                  <div 
                    key={user.user_id} 
                    className={`bg-white rounded-lg p-3 border ${
                      hasChange ? 'border-amber-300 bg-amber-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 bg-gray-500 rounded-full flex items-center justify-center text-white font-medium text-xs">
                          {user.user_name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex items-center gap-2">
                          <Typography variant="mediumParagraph" color="#1F2937" fontWeight={500}>
                            {user.user_name}
                          </Typography>
                          {hasChange && (
                            <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">
                              Modified
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => onMoveUser(user.user_id, 'up')}
                          disabled={isHighest}
                          className="w-7 h-7 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Move up (higher priority)"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => onMoveUser(user.user_id, 'down')}
                          className="w-7 h-7 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 flex items-center justify-center transition-colors"
                          title="Move down (lower priority)"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => onRemoveUser(user.user_id)}
                          className="w-7 h-7 rounded-full bg-red-100 hover:bg-red-200 text-red-600 flex items-center justify-center transition-colors"
                          title="Remove from priority"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Show search results summary */}
          {searchTerm && filteredUsers.length > 0 && filteredUsers.length < users.length && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <Typography variant="smallParagraph" color="#6B7280">
                Showing {filteredUsers.length} of {users.length} users. 
                <button 
                  onClick={() => setSearchTerm('')}
                  className="ml-1 text-blue-600 hover:text-blue-800 underline"
                >
                  Clear search
                </button>
              </Typography>
            </div>
          )}
        </div>
      )}
    </div>
  );
} 